import type React from 'react';
import {useEffect} from 'react';
import {POSTER_EDITOR_NAVBAR_HEIGHT} from '@Components/poster-editor/poster-editor.types';
import type {PopupAnchorRect} from './poster-popup-menu.types';
import type {MenuRowBounds} from './poster-popup-menu.library';
import {getHalfCutMenuHeight} from './poster-popup-menu.library';

const SEEKBAR_TOP = 120;
const POPUP_VERTICAL_MARGIN = 30;
const BOTTOM_MARGIN = 16;
// Below this width we center the popup instead of anchoring it to the click point.
const MIN_WINDOW_WIDTH_FOR_ANCHORED_POPUP = 550;
const ANCHOR_GAP = 8;
const SIDE_MARGIN = 8;

/**
 * Keeps the popup inside the viewport (below the nav bar and above the seekbar).
 * On narrow screens it centers the popup instead of anchoring it.
 *
 * Runs on every render — the original implementation did the same, since the
 * popup's size can change as items are toggled on/off.
 */
export const usePopupPosition = (popUpRef: React.RefObject<HTMLDivElement | null>, anchorRect: PopupAnchorRect | null): void => {
  useEffect(() => {
    const el = popUpRef.current;
    if (!el) {
      return;
    }
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (!windowHeight || !windowWidth) {
      return;
    }

    const spaceBetweenNavBarAndSeekbar = windowHeight - POSTER_EDITOR_NAVBAR_HEIGHT - SEEKBAR_TOP;
    const maxHeight = spaceBetweenNavBarAndSeekbar - POPUP_VERTICAL_MARGIN;
    // eslint-disable-next-line react-compiler/react-compiler -- DOM mutation via ref inside effect is intentional
    el.style.maxHeight = `${getHalfCutMenuHeight(getMenuRowBounds(el), el.scrollHeight, maxHeight)}px`;

    if (windowWidth > MIN_WINDOW_WIDTH_FOR_ANCHORED_POPUP) {
      // Clear any translate transform left over from the narrow-screen branch, otherwise
      // the previous -50%/-50% offsets would be reapplied on top of the anchored position.
      el.style.transform = '';
      if (anchorRect) {
        placeNextToAnchor(el, anchorRect, maxHeight);
        return;
      }
      const boundingRect = el.getBoundingClientRect();
      const initialPosX = boundingRect.left;
      const initialPosY = boundingRect.top;
      let posX = initialPosX;
      let posY = initialPosY;

      if (boundingRect.bottom > windowHeight - BOTTOM_MARGIN) {
        posY = windowHeight - boundingRect.height - BOTTOM_MARGIN;
      }
      if (boundingRect.right > windowWidth) {
        posX = windowWidth - boundingRect.width;
      }

      if (initialPosX !== posX || initialPosY !== posY) {
        el.style.top = `${posY}px`;
        el.style.left = `${posX}px`;
      }
    } else {
      el.style.top = `${spaceBetweenNavBarAndSeekbar / 2 + POSTER_EDITOR_NAVBAR_HEIGHT}px`;
      el.style.left = `50%`;
      el.style.transform = 'translate(-50%, -50%)';
    }
  });
};

/**
 * Drops the menu below the anchor, or above it when there is less room below, and caps its height to the side it
 * opens on so it scrolls rather than sliding over the anchor. Sets the transform origin the open animation grows from.
 */
const placeNextToAnchor = (el: HTMLDivElement, anchorRect: PopupAnchorRect, maxHeight: number): void => {
  const minTop = POSTER_EDITOR_NAVBAR_HEIGHT + ANCHOR_GAP;
  const roomBelow = window.innerHeight - BOTTOM_MARGIN - (anchorRect.bottom + ANCHOR_GAP);
  const roomAbove = anchorRect.top - ANCHOR_GAP - minTop;
  const menuHeight = Math.min(el.scrollHeight, maxHeight);
  const opensBelow = menuHeight <= roomBelow || roomBelow >= roomAbove;
  const height = getHalfCutMenuHeight(getMenuRowBounds(el), el.scrollHeight, Math.min(maxHeight, opensBelow ? roomBelow : roomAbove));
  const width = el.getBoundingClientRect().width;

  el.style.maxHeight = `${height}px`;
  el.style.top = `${opensBelow ? anchorRect.bottom + ANCHOR_GAP : anchorRect.top - ANCHOR_GAP - height}px`;
  el.style.left = `${Math.max(SIDE_MARGIN, Math.min(anchorRect.left, window.innerWidth - width - SIDE_MARGIN))}px`;
  el.style.transformOrigin = opensBelow ? 'top left' : 'bottom left';
};

/**
 * The menu's rows, measured from the top of its scrollable content. Uses layout offsets, not client rects, because
 * the open animation scales the menu and would shrink every measurement. Submenus are portaled out, so only main rows count.
 */
const getMenuRowBounds = (el: HTMLDivElement): MenuRowBounds[] => {
  return Array.from(el.querySelectorAll('li'), (row): MenuRowBounds => {
    return {top: getOffsetTopWithin(row, el), height: row.offsetHeight};
  });
};

const getOffsetTopWithin = (row: HTMLElement, container: HTMLElement): number => {
  let top = 0;
  let node: HTMLElement | null = row;
  while (node && node !== container) {
    top += node.offsetTop;
    const parent: Element | null = node.offsetParent;
    node = parent instanceof HTMLElement ? parent : null;
  }
  return top;
};
